import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import CourseCard from "../components/CourseCard";
import ProfileCard from "../components/ProfileCard";
import { Loader2, BookOpen, GraduationCap, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) { navigate("/login", { replace: true }); return; }
    if (user) {
      api.get(`/my/courses?lang=${lang}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }
  }, [user, authLoading, lang, navigate]);

  if (authLoading || loading) {
    return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><Loader2 className="animate-spin text-[#F5842B]" /></div>;
  }

  const courses = data?.courses || [];
  const completed = courses.filter((c) => c.completed).length;
  const avgProgress = courses.length ? Math.round(courses.reduce((a, c) => a + c.progress_percent, 0) / courses.length) : 0;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      <Navbar />
      <section className="pt-28 pb-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-2xl border border-white/10 object-cover" />
          ) : (
            <div className="h-16 w-16 rounded-2xl adj-gradient flex items-center justify-center text-2xl font-bold">{user?.name?.[0] || "U"}</div>
          )}
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#F5842B]/90">{t("dashboard.eyebrow")}</span>
            <h1 className="font-display text-3xl font-extrabold" data-testid="dashboard-welcome">{t("dashboard.hello")}, {user?.name?.split(" ")[0] || "Studente"}</h1>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          {[[BookOpen, courses.length, t("dashboard.stat1")], [GraduationCap, completed, t("dashboard.stat2")], [TrendingUp, `${avgProgress}%`, t("dashboard.stat3")]].map(([Icon, n, l], i) => (
            <div key={i} className="glass rounded-2xl p-6 flex items-center gap-4" data-testid={`stat-${i}`}>
              <div className="h-12 w-12 rounded-xl adj-gradient flex items-center justify-center"><Icon size={22} className="text-white" /></div>
              <div>
                <div className="font-display text-3xl font-extrabold">{n}</div>
                <div className="text-xs text-slate-400">{l}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <ProfileCard />
        <h2 className="font-display text-2xl font-bold mb-6">{t("dashboard.myCourses")}</h2>
        {courses.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center" data-testid="dashboard-empty">
            <BookOpen size={40} className="mx-auto text-slate-600" />
            <p className="text-slate-400 mt-4">{t("dashboard.empty")}</p>
            <button onClick={() => navigate("/#corsi")} className="mt-6 adj-gradient text-white font-semibold px-7 py-3 rounded-full hover:opacity-90 transition-opacity" data-testid="dashboard-explore-btn">
              {t("dashboard.explore")}
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {courses.map((c, i) => <CourseCard key={c.slug} course={c} index={i} />)}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
